import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import FileUploader from '../components/document/FileUploader';
import FolderManager from '../components/document/FolderManager';
import ErrorMessage from '../components/common/ErrorMessage';
import './UploadPage.css';

const UploadPage: React.FC = () => {
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>();
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedDocument, setUploadedDocument] = useState<any>(null);
  const [error, setError] = useState('');

  const { user } = useAuth();
  const navigate = useNavigate();

  const handleUploadSuccess = (document: any) => {
    setUploadedDocument(document);
    setUploadSuccess(true);
    setError('');
  };

  const handleUploadError = (errorMessage: string) => {
    setError(errorMessage);
    setUploadSuccess(false);
  };

  const handleFolderChange = (folderId?: string, _folderName?: string) => {
    setSelectedFolderId(folderId);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (uploadSuccess && uploadedDocument) {
    return (
      <div className="page-container">

        <div className="d-flex justify-content-center">
          <div className="card" style={{ maxWidth: '600px', width: '100%' }}>
            <div className="card-body">
              <div className="alert alert-success">
                <h5>Document uploaded successfully!</h5>
                <p>Your document is now available in your library and ready to share.</p>
              </div>

              <div className="document-details mb-4">
                <h6>Document Details</h6>
                <div className="detail-grid">
                  <div className="detail-row">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{uploadedDocument.name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Size:</span>
                    <span className="detail-value">{formatFileSize(uploadedDocument.fileSize)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Pages:</span>
                    <span className="detail-value">{uploadedDocument.pageCount}</span>
                  </div>
                  {uploadedDocument.folderId && (
                    <div className="detail-row">
                      <span className="detail-label">Folder:</span>
                      <span className="detail-value">📁 Selected folder</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="action-buttons">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/documents/${uploadedDocument.documentId}`)}
                >
                  View Document
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setUploadSuccess(false);
                    setUploadedDocument(null);
                  }}
                >
                  Upload Another
                </button>
              </div>

              {user && (
                <div className="next-steps mt-4">
                  <h6>What's Next?</h6>
                  <ul className="next-steps-list">
                    <li>
                      <strong>Share your document:</strong> Set permissions and share with specific users
                    </li>
                    <li>
                      <strong>Organize:</strong> Move to folders or create new organizational structure
                    </li>
                    <li>
                      <strong>Track usage:</strong> Monitor who views your document and when
                    </li>
                    <li>
                      <strong>Control access:</strong> Set page-level permissions and download restrictions
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {error && (
        <ErrorMessage 
          message={error} 
          onDismiss={() => setError('')} 
        />
      )}

      <div className="upload-layout">
        {/* Main Upload Area */}
        <div className="upload-main">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">
                {selectedFolderId ? '📁 Upload to Selected Folder' : '📄 Upload Document'}
              </h5>
              
              {selectedFolderId && (
                <div className="selected-folder-info">
                  <small className="text-muted">
                    Document will be uploaded to the selected folder
                  </small>
                </div>
              )}

              <FileUploader
                folderId={selectedFolderId}
                onUploadSuccess={handleUploadSuccess}
                onUploadError={handleUploadError}
                showProgress={true}
              />
            </div>
          </div>

          {/* Upload Features */}
          <div className="upload-features">
            <h6>Upload Features</h6>
            <div className="features-grid">
              <div className="feature-item">
                <div className="feature-icon">🔒</div>
                <div className="feature-content">
                  <h6>Secure Storage</h6>
                  <p>Documents encrypted at rest with AWS KMS</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">📊</div>
                <div className="feature-content">
                  <h6>Page-Level Analytics</h6>
                  <p>Track which pages are viewed and for how long</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">🔗</div>
                <div className="feature-content">
                  <h6>Secure Sharing</h6>
                  <p>Control who can view, download, or print</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">⚡</div>
                <div className="feature-content">
                  <h6>Fast Access</h6>
                  <p>Page-level streaming for optimal performance</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Folder Sidebar */}
        {user && (
          <div className="upload-sidebar">
            <FolderManager
              currentFolderId={selectedFolderId}
              onFolderChange={handleFolderChange}
            />
          </div>
        )}
      </div>

      {/* Anonymous User Upgrade Prompt */}
      {!user && (
        <div className="upgrade-prompt">
          <div className="card">
            <div className="card-body text-center">
              <h5>🚀 Want More Features?</h5>
              <p>Create a free account to unlock:</p>
              <div className="upgrade-features">
                <div className="upgrade-feature">
                  <span className="upgrade-icon">💾</span>
                  <span>Unlimited file size & permanent storage</span>
                </div>
                <div className="upgrade-feature">
                  <span className="upgrade-icon">📁</span>
                  <span>Folder organization & management</span>
                </div>
                <div className="upgrade-feature">
                  <span className="upgrade-icon">👥</span>
                  <span>Advanced sharing & collaboration</span>
                </div>
                <div className="upgrade-feature">
                  <span className="upgrade-icon">📈</span>
                  <span>Detailed analytics & insights</span>
                </div>
              </div>
              <div className="upgrade-actions">
                <button
                  className="btn btn-success"
                  onClick={() => navigate('/register')}
                >
                  Create Free Account
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadPage;